import { useMemo, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Play, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

const WEBHOOK_URL =
  'https://primary-production-bd89.up.railway.app/webhook-test/38330197-318a-4041-8658-95667d9e87da'

const DEFAULT_JSON_PAYLOAD = {
  headers: {
    'x-railway-edge': 'railway/us-east4-eqdc4a',
    'x-railway-request-id': 'zOTwXzyhSGqbqF0p8u2xcg',
    'x-real-ip': '129.153.135.244',
    'x-request-start': '1778079102961',
    'accept-encoding': 'gzip',
  },
  params: {},
  query: {},
  body: {
    data: {
      action: 'card.create',
      on_phase: {
        id: 316433090,
        name: 'Aprovação Proposta Gestor',
      },
      created_by: {
        id: 305517952,
        name: 'Isabelle Bastos Marcelo',
        username: 'isabelle-bastos-solisinvestimentos',
        email: 'isabelle.bastos@solisinvestimentos.com.br',
        avatar_url:
          'https://gravatar.com/avatar/d43d01e3a1c871b9a9abd60e475f035b.png?s=144&d=https://pipestyle.staticpipefy.com/v2-temp/illustrations/avatar.png',
      },
      card: {
        id: 1347017230,
        title: 'Verba de Treinamento - Pos Graduação Gestão de Pessoas',
        pipe_id: 'E2YEJXIU',
      },
    },
  },
  webhookUrl: 'https://primary-production-bd89.up.railway.app/webhook/38330197-318a-4041-8658-95667d9e87da',
  executionMode: 'production',
}

export default function DisparadorWebhookPage() {
  const [payloadText, setPayloadText] = useState(
    JSON.stringify(DEFAULT_JSON_PAYLOAD, null, 2)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [responseBody, setResponseBody] = useState<string>('')
  const [responseStatus, setResponseStatus] = useState<number | null>(null)

  const parsedPayload = useMemo(() => {
    try {
      return { value: JSON.parse(payloadText), error: null as string | null }
    } catch (error: any) {
      return { value: null, error: error?.message || 'JSON inválido' }
    }
  }, [payloadText])

  const handleReset = () => {
    setPayloadText(JSON.stringify(DEFAULT_JSON_PAYLOAD, null, 2))
    setResponseBody('')
    setResponseStatus(null)
  }

  const handleDisparar = async () => {
    if (parsedPayload.error || !parsedPayload.value) {
      toast.error('JSON inválido. Corrija o conteúdo antes de disparar.')
      return
    }

    setIsSubmitting(true)
    setResponseBody('')
    setResponseStatus(null)

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parsedPayload.value),
      })

      const responseText = await response.text()
      setResponseStatus(response.status)
      setResponseBody(responseText || '(sem conteúdo na resposta)')

      if (response.ok) {
        toast.success(`Webhook disparado com sucesso (HTTP ${response.status}).`)
      } else {
        toast.error(`Webhook retornou erro (HTTP ${response.status}).`)
      }
    } catch (error: any) {
      const message = error?.message || 'Falha ao chamar o webhook.'
      setResponseBody(message)
      toast.error(`Erro de rede: ${message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Disparador n8n</h1>
        <p className="text-sm text-muted-foreground">
          Envie um JSON customizado via POST para o webhook configurado.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuração do Disparo</CardTitle>
          <CardDescription>
            URL fixa: <span className="font-mono text-xs">{WEBHOOK_URL}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Validação JSON:</span>
              {parsedPayload.error ? (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Inválido
                </Badge>
              ) : (
                <Badge variant="default">Válido</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleReset} disabled={isSubmitting}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Restaurar exemplo
              </Button>
              <Button onClick={handleDisparar} disabled={isSubmitting || !!parsedPayload.error}>
                <Play className="mr-2 h-4 w-4" />
                {isSubmitting ? 'Disparando...' : 'Disparar webhook'}
              </Button>
            </div>
          </div>

          <Textarea
            value={payloadText}
            onChange={(event) => setPayloadText(event.target.value)}
            className="min-h-[420px] font-mono text-xs"
            placeholder="Cole aqui o JSON que será enviado no POST..."
          />

          {parsedPayload.error && (
            <p className="text-sm text-destructive">Erro no JSON: {parsedPayload.error}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resposta do Webhook</CardTitle>
          <CardDescription>
            {responseStatus !== null ? `Status HTTP: ${responseStatus}` : 'Nenhum disparo realizado ainda.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-[320px] overflow-auto rounded-md border bg-muted p-3 text-xs">
            {responseBody || 'Aguardando chamada...'}
          </pre>
        </CardContent>
      </Card>
    </div>
  )
}

